import { BODY_PRINT_SHAPES } from './bodyShapes';
import type { BodyRegion } from '../Notfall/notfallData';

/**
 * The body figure — for paper only.
 *
 * Stage C established that a standing figure cannot carry safe touch targets on screen: in the
 * 708 × 326 px the Notfall panel gets, a 200 × 420 figure renders at scale 0.776, so a 16 mm target
 * would need 107 viewBox units and only 1.9 fit across. The screen therefore uses a labelled grid.
 *
 * On A4 the same figure has 90 mm of width and no touch floor to respect — nothing activates here,
 * and a partner interprets a gesture rather than a coordinate. So the design's original body-map
 * instinct was right; it was right about **paper**.
 *
 * **Numbered regions with a legend, not floating labels.** The first version placed labels in two
 * columns by index and produced "Arm links" beside the hips. A number inside its own shape cannot
 * drift; a label beside a figure can, and nobody notices until someone is pointing at it in pain.
 */
export function BodyFigure({
  side,
  regions,
}: {
  side: 'front' | 'back';
  regions: BodyRegion[];
}): React.JSX.Element {
  const placed = regions
    .map((region, index) => ({ region, number: index + 1, shape: BODY_PRINT_SHAPES[region.id] }))
    .filter((entry): entry is { region: BodyRegion; number: number; shape: NonNullable<typeof entry.shape> } =>
      entry.shape !== undefined,
    );

  return (
    <>
      <svg
        viewBox="0 0 100 200"
        role="img"
        aria-label={side === 'front' ? 'Körper vorne' : 'Körper hinten'}
      >
        {placed.map(({ region, number, shape }) => {
          const cx = shape.x + shape.width / 2;
          const cy = shape.y + shape.height / 2;
          return (
            <g key={region.id}>
              <rect
                x={shape.x}
                y={shape.y}
                width={shape.width}
                height={shape.height}
                rx={shape.radius ?? 4}
                fill="none"
                stroke="#000"
                strokeWidth="1"
              />
              <text
                x={cx}
                y={cy + 3}
                fontSize="8"
                fontWeight="700"
                textAnchor="middle"
                fill="#000"
              >
                {number}
              </text>
            </g>
          );
        })}
      </svg>

      {/* The legend gives the partner the word to say. The person can point at either. */}
      <ol className="kit-legend">
        {placed.map(({ region, number }) => (
          <li key={region.id}>
            <span className="kit-legend-number">{number}</span>
            {region.label}
          </li>
        ))}
      </ol>
    </>
  );
}
